import { inject } from "fw";
import { createFrom, createFromArray } from "fw-model";

import { Manager } from "./manager";
import { LoginResult } from "models/misc";
import { ChangeEmail, User } from "models/user";

@inject
export class UserRepository {
    constructor(private s: Manager) {}

    // returns the token
    public async login(args: LoginArgs) {
        const q = this.s.tokenLifespan ? { tokenLifespan: this.s.tokenLifespan } : null;
        const res = await this.s.post<LoginResult>("user/login", args, q);
        this.s.setToken(res.body.BearerToken);

        return res.body.BearerToken;
    }

    public async changeEmail(token: string): Promise<ChangeEmail> {
        const res = await this.s.get<ChangeEmail>(`user/change-email/${token}`);
        return createFrom(ChangeEmail, res.body);
    }

    public async confirmChangeEmail(password: string, token: string) {
        await this.s.post("user/confirm-change-email", { password, token });
    }

    public async cancelChangeEmail(id: string) {
        const res = await this.s.delete(`user/${id}/cancel-change-email`);
        return createFrom(User, res.body);
    }

    public async resendChangeEmail(id: string) {
        await this.s.post(`user/${id}/resend-change-email`, null);
    }

    public async switchOrganization(organizationId: string) {
        const q = {
            organizationId,
            tokenLifespan: this.s.tokenLifespan || null
        };
        const res = await this.s.get<LoginResult>("user/switch-organization", {
            organizationId,
            q
        });
        this.s.setToken(res.body.BearerToken);

        return res.body.BearerToken;
    }

    public async forgotPassword(EmailAddress: string) {
        await this.s.post("user/request-password-change", { EmailAddress });
    }

    public async resetPassword(Password: string, Token: string) {
        await this.s.post("user/change-password", { Password, Token });
    }

    public async refreshToken() {
        const q = this.s.tokenLifespan ? { tokenLifespan: this.s.tokenLifespan } : null;
        const res = await this.s.get<LoginResult>("user/refresh-token", q);
        this.s.setToken(res.body.BearerToken);
        return res.body.BearerToken;
    }

    public logout() {
        this.s.deleteToken();
    }

    async list(f = null, s = null, sort = null, page = 1, pageSize = 20) {
        const res = await this.s.get<any[]>("user", { f, s, sort, page, pageSize });
        return { list: createFromArray(User, res.body), total: parseInt(res.headers["x-manager-total"], 10) };
    }

    public async me() {
        const res = await this.s.get("user/me");
        return createFrom(User, res.body);
    }

    public async getIds(ids: string[]) {
        const res = await this.s.post<any[]>("user/batch", ids, { skipMissing: true });
        return createFromArray(User, res.body);
    }

    public async get(id: string) {
        const res = await this.s.get(`user/${id}`);
        return createFrom(User, res.body);
    }

    public async put(user: User) {
        const q = this.s.tokenLifespan ? { tokenLifespan: this.s.tokenLifespan } : null;
        const res = await this.s.put(`user/${user.Id}`, user, q);
        return createFrom(User, res.body);
    }

    public async post(user: User) {
        const res = await this.s.post("user", user);
        return createFrom(User, res.body);
    }

    async webSocketKey() {
        const res = await this.s.post<string>("user/websocket-key", null);
        return res.body;
    }
}

interface UpdateEmailArgs {
    OrganizationId: string;
    EmailAddress: string;
    Password: string;
}

interface UpdatePasswordArgs {
    NewPassword: string;
    Password: string;
}

interface LoginArgs {
    OrganizationId?: string;
    EmailAddress: string;
    Password: string;
    Token?: string;
    Code?: string;
}
